import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';

import classNames from 'classnames';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { ActionId } from 'admin/actions/Actions';
import { SourceId } from 'admin/sources/Sources';
import { isAuthorized } from 'auth/Auth';
import { sendConversation } from 'common/comments2/Comments2';
import { DataType, PublishStatus } from 'common/comments2/Comments2.types';
import { Operation } from 'common/util/ApiCommon';
import { Overlay } from 'core/overlay/Overlay';
import { ReviewActionsMenu as ReplyActionsMenu, DropdownAction } from 'reviews2/feed/review-yeti/actions/ActionsMenu';
import { RepliesActionType, useReplies } from 'reviews2/feed/review-yeti/Replies.store';
import { ResponseState, UserType } from 'reviews2/feed/review-yeti/reply/ReviewReply.types';
import { shouldManualPublish } from 'reviews2/feed/review-yeti/reply/ReviewReply.util';
import { ContextProps, ReadOnlyReason } from 'reviews2/feed/review-yeti/Review.types';
import { deleteResponse } from 'reviews2/Reviews.api';

interface Props extends ContextProps {
    reply: any;
}

enum ReplyActionId {
    Hide = 1,
    Delete,
}

const OVERLAY_AREA = 'reply-actions-container';

export const ReplyActionsContainer: FunctionComponent<Props> = ({ context, reply }) => {
    const { t } = useTranslation('reviews2');
    const { options, review } = context;
    const sourceId = reply.review_source_id;
    const [isHidden, setIsHidden] = useState(reply.properties?.isHidden?.booleanValues?.[0]);
    const canHide = reply.properties?.canHide?.booleanValues?.[0];
    const [{ disableModeration }, dispatch] = useReplies();

    const actionFn = useCallback(
        async (event, action) => {
            const manualPublish = shouldManualPublish(review);

            switch (action.id) {
                case ReplyActionId.Hide: {
                    const updatedReply = {
                        ...reply,
                        properties: {
                            ...reply.properties,
                            isHidden: { name: 'isHidden', dataType: DataType.Boolean, booleanValues: [!isHidden] },
                        },
                    };
                    const { comment } = await trackPromise(
                        sendConversation(reply.tenantID, {
                            comment: updatedReply,
                            manualPublish,
                            operation: Operation.Update,
                        }),
                        OVERLAY_AREA,
                    );

                    setIsHidden(!isHidden);
                    dispatch({
                        type: RepliesActionType.UpdateReply,
                        reply: comment,
                    });
                    return;
                }
                case ReplyActionId.Delete: {
                    await trackPromise(deleteResponse(reply.r4e_tenant_id, reply.document_id), OVERLAY_AREA);

                    dispatch({
                        type: RepliesActionType.RemoveReply,
                        reply,
                    });
                    return;
                }
                default:
                    throw new Error(`Unknown action id: ${action.id}`);
            }
        },
        [dispatch, isHidden, reply, review],
    );

    const actions = useMemo(() => {
        const menuActions: DropdownAction[] = [];

        if (
            options!.readOnly &&
            [ReadOnlyReason.View, ReadOnlyReason.Reports].includes(options!.readOnlyReason as ReadOnlyReason)
        ) {
            return menuActions;
        }

        if (
            options?.repliesOptions?.showDelete &&
            isAuthorized({ actions: [ActionId.ReviewResponses.Delete] }) &&
            (isEqual(sourceId, SourceId.Facebook) ||
                (reply.publishStatus || 0) >= PublishStatus.AuthorizationInvalid ||
                [
                    ResponseState.Failed,
                    ResponseState.Rejected,
                    ResponseState.PendingManual,
                    ResponseState.PendingApproval,
                    ResponseState.PendingModeration,
                ].includes(reply.responseState))
        ) {
            menuActions.push({
                id: ReplyActionId.Delete,
                label: t('DELETE'),
            });
        }

        if (
            isAuthorized({ actions: [ActionId.ReviewResponses.Hide] }) &&
            reply.author_user_type === UserType.User &&
            canHide
        ) {
            menuActions.push({
                id: ReplyActionId.Hide,
                label: isHidden ? t('UNHIDE') : t('HIDE'),
            });
        }

        return menuActions;
    }, [t, reply, options, sourceId, canHide, isHidden]);

    return (
        <div className={classNames('r4e-review-reply-actions-container', { 'is-hidden': disableModeration })}>
            <Overlay area={OVERLAY_AREA} />
            <ReplyActionsMenu actions={actions} actionFn={actionFn} />
        </div>
    );
};
